import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { Tooltip } from '@mui/material';

import {
  deleteCoveragePlan,
  getCoverageOffers,
  updateCoverageOffer,
} from 'src/store/coverageBuilder';
import { getAccountProposalSelector } from 'src/store/proposals';

import { Button, EmptyField, LogoLoader, LoaderWrapper, Modal, Status } from 'src/components';
import { CardOffers } from '../CardOffers/CardOffers';

import { MODAL_NAMES } from 'src/constants/modalNames';

import classes from 'src/sass/styles/Proposal/proposal.module.scss';

export const ProposalCard = (props) => {
  const { onClickConfigDetails, config, type = 'proposal', build } = props;

  const { card_options, coverage } = build;

  const dispatch = useDispatch();
  const params = useParams();
  const { pathname } = useLocation();

  const accountProposalData = useSelector(getAccountProposalSelector);

  const accountId = params?.id ? params?.id : accountProposalData?.account_id;
  const proposalId = params?.proposalId ? params?.proposalId : accountProposalData?.id;

  const read_token = params?.token;

  const isReadOnlyMode = pathname.includes('/read-only');

  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState(false);
  const [isVisibleCardContent, setIsVisibleCardContent] = useState(false);
  const [offers, setOffers] = useState([]);
  const [isLoadingOffers, setIsLoadingOffers] = useState(false);

  const [isInitialLoading, setIsInitialLoading] = useState(false);

  const fetchCoveregeOffers = useCallback(() => {
    dispatch(
      getCoverageOffers({
        accountId,
        proposalId,
        coverage,
        configId: config.id,
        brokerConfig: config,
        setOffers,
        setIsLoading: setIsLoadingOffers,
        read_token,
        params: type === 'quote' ? { 'f[status]': 'selected,approved', token: read_token } : {},
      }),
    );
  }, [dispatch, accountId, config, coverage, proposalId, type, read_token]);

  useEffect(() => {
    setIsInitialLoading(offers.length === 0);
  }, [offers.length]);

  useEffect(() => {
    if (isVisibleCardContent) {
      fetchCoveregeOffers();
    } else {
      setOffers([]);
    }
  }, [fetchCoveregeOffers, isVisibleCardContent]);

  const onCardClick = useCallback(() => {
    setIsVisibleCardContent((prev) => !prev);
  }, []);

  const onShowDeleteModal = useCallback(() => {
    setIsVisibleDeleteModal(true);
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    setIsVisibleDeleteModal(false);
  }, []);

  const onClickDelete = useCallback(async () => {
    if (type === 'proposal') {
      return dispatch(deleteCoveragePlan({ accountId, proposalId, coverage, planId: config?.id }));
    }

    const response = await dispatch(
      getCoverageOffers({
        accountId,
        proposalId,
        coverage,
        configId: config.id,
        brokerConfig: config,
        setOffers,
        setIsLoading: setIsLoadingOffers,
        read_token,
        params: type === 'quote' ? { 'f[status]': 'selected,approved', token: read_token } : {},
      }),
    );

    const fetchedOffers = response.payload;

    for (const offer of fetchedOffers) {
      const payload = {
        ...offer,
        accountId,
        proposalId,
        isBroker: true,
        status: 'completed',
      };

      dispatch(updateCoverageOffer({ payload, coverage, offerId: payload.id, read_token }));
    }
  }, [dispatch, type, accountId, proposalId, coverage, config, read_token]);

  const onSelectCoverageOffer = useCallback(
    (item, status) => async () => {
      const payload = { ...item, accountId, proposalId };

      payload.status = status;
      delete payload.calculation;

      await dispatch(updateCoverageOffer({ payload, coverage, offerId: payload.id }));
      await fetchCoveregeOffers();
    },
    [dispatch, fetchCoveregeOffers, accountId, proposalId, coverage],
  );

  const coverageOffers = offers?.filter((item) => item.issuer_name !== 'broker');
  const brokerConfig = offers?.find((item) => item.issuer_name === 'broker');

  return (
    <>
      {isVisibleDeleteModal && (
        <Modal type="small" closeButtonType="inside" onClose={onCloseDeleteModal}>
          {isLoadingOffers ? (
            <LoaderWrapper>
              <LogoLoader />
            </LoaderWrapper>
          ) : (
            <div className={classes.ModalContent}>
              <div className={classes.ModalContentTitle}>
                {type === 'proposal' ? MODAL_NAMES.DELETE_QUOTE : MODAL_NAMES.EXCLUDE_QUOTE}
              </div>
              <div className={classes.ModalContentFooter}>
                <Button type="secondary" title="Cancel" onClick={onCloseDeleteModal} />
                <Button type="danger" title="Yes" onClick={onClickDelete} />
              </div>
            </div>
          )}
        </Modal>
      )}

      <div className={classes.Proposal}>
        <div className={classes.ProposalHeader} onClick={onCardClick}>
          <div className={classes.HeaderIcon}>
            <div className={classes.HeaderIconWrapper}>
              <i
                className={`fas fa-chevron-down ${
                  isVisibleCardContent
                    ? classes.ProposalHeaderIconActive
                    : classes.ProposalHeaderIcon
                }`}
              />
            </div>
          </div>

          <div className={classes.ProposalHeaderWrapper}>
            {card_options?.proposal?.map((option) => {
              const rawValue = config?.[option.key];
              const value = option?.format ? option.format(rawValue) : rawValue;

              return (
                <React.Fragment key={option.key}>
                  <div className={classes.ProposalHeaderItem}>
                    <div className={classes.ProposalHeaderItemHeading}>
                      <Tooltip title={option.title}>
                        <span>{option.title}</span>
                      </Tooltip>
                    </div>

                    <div className={classes.ProposalHeaderItemTitle}>
                      <Tooltip title={value ?? ''}>
                        <span>{value ?? ''}</span>
                      </Tooltip>
                    </div>
                  </div>

                  {option?.render &&
                    option.render(rawValue)?.map((renderItem, idx) => {
                      const rRawValue = config?.[renderItem.key];
                      const rValue = renderItem?.format ? renderItem.format(rRawValue) : rRawValue;

                      return (
                        <div
                          className={classes.ProposalHeaderItem}
                          key={`${option.key}:${renderItem.key}:${idx}`}
                        >
                          <div className={classes.ProposalHeaderItemHeading}>
                            <Tooltip title={renderItem.title}>
                              <span>{renderItem.title}</span>
                            </Tooltip>
                          </div>

                          <div className={classes.ProposalHeaderItemTitle}>
                            <Tooltip title={rValue ?? ''}>
                              <span>{rValue ?? ''}</span>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                </React.Fragment>
              );
            })}

            <div className={classes.ProposalHeaderButtons}>
              <div className={classes.ProposalHeaderStatus}>
                <Status status={config.status} />
              </div>

              {!isReadOnlyMode && type === 'proposal' && (
                <Button
                  title="Config Details"
                  type="secondary"
                  onClick={onClickConfigDetails(config)}
                />
              )}
            </div>
          </div>
        </div>

        {!isReadOnlyMode && (
          <div className={classes.ProposalDeleteButton} onClick={onShowDeleteModal}>
            <i className="fa fa-trash-o" />
          </div>
        )}
      </div>

      {isVisibleCardContent && (
        <>
          {isLoadingOffers && isInitialLoading ? (
            <div className={classes.LoaderCard}>
              <LoaderWrapper>
                <LogoLoader />
              </LoaderWrapper>
            </div>
          ) : coverageOffers?.length ? (
            <CardOffers
              type={type}
              build={build}
              offers={coverageOffers}
              brokerConfig={brokerConfig}
              fetchOffers={fetchCoveregeOffers}
              onSelectCoverageOffer={onSelectCoverageOffer}
            />
          ) : (
            <EmptyField title="No offers received" />
          )}
        </>
      )}
    </>
  );
};
